import React from 'react';

interface GemIconProps {
  className?: string;
  size?: number | string;
  color?: 'blue' | 'cyan' | 'flatCyan' | 'white' | 'flatWhite' | 'black';
}

const GemIcon: React.FC<GemIconProps> = ({ className = '', size = 24, color = 'blue' }) => {
  // Define color schemes
  const colorSchemes = {
    blue: {
      gradient: ['#60A5FA', '#3B82F6', '#1D4ED8'],
      highlight: ['#93C5FD', '#3B82F6'],
      facetLeft: '#2563EB',
      facetRight: '#1E40AF',
    },
    cyan: {
      gradient: ['#67E8F9', '#06B6D4', '#0E7490'],
      highlight: ['#A5F3FC', '#06B6D4'],
      facetLeft: '#0891B2',
      facetRight: '#155E75',
    },
    flatCyan: {
      gradient: ['#06B6D4', '#06B6D4', '#06B6D4'],
      highlight: ['#06B6D4', '#06B6D4'],
      facetLeft: '#06B6D4',
      facetRight: '#06B6D4',
    },
    white: {
      gradient: ['#FFFFFF', '#F3F4F6', '#E5E7EB'],
      highlight: ['#FFFFFF', '#F9FAFB'],
      facetLeft: '#F3F4F6',
      facetRight: '#E5E7EB',
    },
    flatWhite: {
      gradient: ['#FFFFFF', '#FFFFFF', '#FFFFFF'],
      highlight: ['#FFFFFF', '#FFFFFF'],
      facetLeft: '#FFFFFF',
      facetRight: '#FFFFFF',
    },
    black: {
      gradient: ['#4B5563', '#374151', '#1F2937'],
      highlight: ['#6B7280', '#4B5563'],
      facetLeft: '#374151',
      facetRight: '#1F2937',
    },
  };

  const scheme = colorSchemes[color];
  const gradientId = `gemGradient-${color}`;
  const highlightId = `gemHighlight-${color}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Diamond shape with gradient */}
      <defs>
        <linearGradient id={gradientId} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor={scheme.gradient[0]} />
          <stop offset="50%" stopColor={scheme.gradient[1]} />
          <stop offset="100%" stopColor={scheme.gradient[2]} />
        </linearGradient>
        <linearGradient id={highlightId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={scheme.highlight[0]} stopOpacity="0.8" />
          <stop offset="100%" stopColor={scheme.highlight[1]} stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Main gem body */}
      <path
        d="M50 10 L85 35 L50 90 L15 35 Z"
        fill={color === 'flatCyan' ? '#06B6D4' : color === 'flatWhite' ? '#FFFFFF' : `url(#${gradientId})`}
      />
      
      {/* Top facet */}
      <path
        d="M50 10 L85 35 L50 45 L15 35 Z"
        fill={color === 'flatCyan' || color === 'flatWhite' ? (color === 'flatWhite' ? '#FFFFFF' : '#06B6D4') : `url(#${highlightId})`}
      />
      
      {/* Left facet highlight */}
      <path
        d="M15 35 L50 45 L50 90 Z"
        fill={scheme.facetLeft}
        opacity={color === 'flatCyan' || color === 'flatWhite' ? '1' : '0.7'}
      />
      
      {/* Right facet */}
      <path
        d="M85 35 L50 45 L50 90 Z"
        fill={scheme.facetRight}
        opacity={color === 'flatCyan' || color === 'flatWhite' ? '1' : '0.5'}
      />
      
      {/* Shine effect */}
      <ellipse
        cx="35"
        cy="30"
        rx="8"
        ry="5"
        fill="white"
        opacity={color === 'flatCyan' || color === 'flatWhite' ? '0' : color === 'white' ? '0.3' : '0.6'}
      />
    </svg>
  );
};

export default GemIcon;
